import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { loginUser, setAuthError, loadAuthData } from '../store/slices/authSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import s10Logo from '../assets/images/s10Logo.png';
import {
  Button,
  Grid,
  InputAdornment,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MyTextField from '../components/MyTextField';
import LoadingComponent from '../components/LoadingComponent';
import MySnackbar from '../components/MySnackbar';
import { Link, useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';

const Login = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const authState = useSelector((state: RootState) => state.auth);
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }

    if (authState.isLogged === null) {
      dispatch(loadAuthData());
    }
  }, [dispatch, authState.isLogged]);

  useEffect(() => {
    if (authState.isLogged && !authState.isLoading) {
      navigate('/dashboard');
    }
  }, [authState.isLogged, authState.isLoading, navigate]);

  useEffect(() => {
    if (authState.challengeName === 'NEW_PASSWORD_REQUIRED') {
      navigate('/change-password', { state: { required: true } });
    }
  }, [authState.challengeName, navigate]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    if (!username.trim()) {
      setUsernameError('El usuario es requerido');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(username)) {
      setUsernameError('Ingresa un email válido');
      isValid = false;
    } else {
      setUsernameError('');
    }

    if (!password) {
      setPasswordError('La contraseña es requerida');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      isValid = false;
    } else {
      setPasswordError('');
    }

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (rememberMe) {
      localStorage.setItem('rememberedUsername', username);
    } else {
      localStorage.removeItem('rememberedUsername');
    }

    dispatch(loginUser({ UserName: username, Password: password }));
  };

  return (
    <Grid container style={{ height: "100vh", margin: "0 !important" }}>
      <Grid
        container
        item
        xs={12}
        md={6}
        direction="column"
        justifyContent="center"
        alignItems="center"
        style={{ backgroundColor: theme.s10palette?.S10Blueblue50 }}
      >
        <MySnackbar
          open={!!authState.error}
          autoHideDuration={6000}
          msg={authState.error || ''}
          severity="error"
          onClose={() => dispatch(setAuthError(''))}
        />

        <Grid
          container
          direction="row"
          maxWidth="20rem"
          alignItems="center"
          rowSpacing="1rem"
          component="form"
          onSubmit={handleSubmit}
          spacing={2}
        >
          <Grid justifyContent="center" alignItems="center" alignContent="center" item xs={12} style={{ marginTop: ".5rem" }}>
            <img style={{ width: "8.75rem" }} src={s10Logo} alt="S10" />
          </Grid>
          <Typography variant="h3" style={{ marginBottom: "0.5rem" }}>
              ¡Bienvenido!
          </Typography>
          <Typography variant="h6">Portal Mis Boletas - Ingresar</Typography>
          <Divider
            style={{
              width: "100%",
              marginTop: "1.19rem",
              marginBottom: "1.5rem",
              color: theme.s10palette.S10Blueblue100,
            }}
          />
          <Typography variant="body1" align="left" width="100%">
            Ingresa tus credenciales
          </Typography>
          <MyTextField
            id="username"
            label="Usuario"
            type="text"
            name="username"
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
            error={!!usernameError}
            helperText={usernameError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon/>
                </InputAdornment>
              ),
            }}
          />
          <MyTextField
            id="password"
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                color="primary"
                />
              }
              label="Recordarme"
            />

            <Typography variant="body2">
              <Link to="/forgot-password" style={{ color: theme.s10palette?.S10Blueblue600, textDecoration: 'none' }}>
                ¿Olvidaste tu contraseña?
              </Link>
            </Typography>
          </Grid>
          <Grid 
            item 
            sx={{ width: '100%', mt: 2 }}
          >
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={authState.isLoading}
            sx={{
                backgroundColor: theme.s10palette?.S10Blueblue600,
                '&:hover': {
                backgroundColor: theme.s10palette?.S10Blueblue700,
                },
                py: 1.5,
            }}
          >
            Iniciar sesión
          </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid
        item
        md={6}
        sx={{ display: { xs: 'none', md: 'block' } }}
        style={{
          backgroundColor: theme.s10palette?.S10Blueblue700,
          backgroundImage: `url(loginDefaultImage.svg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {authState.isLoading && <LoadingComponent loadingText="Iniciando sesión..." />}
    </Grid>
  );
};

export default Login;